import { useEffect } from "react";
import { ArrowDownRight, Folder, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import {
  PROJECT_TILE_CLASS,
  PROJECT_TILE_HEIGHT_CLASS,
  PROJECT_TILE_INTERACTIVE_CLASS,
} from "@/domains/projects/components/projectTile";
import { ProjectStatusBadge } from "@/domains/projects/status/ProjectStatusBadge";
import { cn } from "@/lib/utils";
import type { BuildingScopeProjectOption } from "@/hooks/buildings/useBuildingScope";
import { useDocumentFiltersOptional } from "../context/DocumentFiltersContext";

/**
 * The Folders tab in Building view. Folders belong to a project (and so do
 * their permissions), so the building-wide entry is a choice of project; the
 * pick enters that project's folder view. The page's search box narrows the
 * projects by name here, and is reported resolved so it never spins.
 */
export function BuildingDocumentFolders({
  projects,
  onOpen,
  loading,
  error,
  onRetry,
}: {
  projects: BuildingScopeProjectOption[];
  onOpen: (projectId: number) => void;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}) {
  const { t } = useLanguage();
  const filters = useDocumentFiltersOptional();
  const searchQuery = filters?.filters.searchQuery ?? "";
  const reportResolved = filters?.reportResolvedSearchQuery;
  useEffect(() => {
    reportResolved?.(searchQuery);
  }, [reportResolved, searchQuery]);
  const needle = searchQuery.trim().toLowerCase();
  const shown = needle
    ? projects.filter((project) => project.name.toLowerCase().includes(needle))
    : projects;
  if (error) {
    return (
      <div role="alert" className="flex min-h-48 flex-col items-center justify-center gap-3">
        <p className="text-sm text-muted-foreground">{t.buildings.scope.loadFailed}</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t.common.retry}
        </Button>
      </div>
    );
  }
  if (loading) {
    return (
      <div
        role="status"
        aria-label={t.common.loading}
        className="flex min-h-48 items-center justify-center"
      >
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
      </div>
    );
  }
  if (shown.length === 0) {
    return <p className="p-6 text-sm text-muted-foreground">{t.projects.selector.noProjects}</p>;
  }
  return (
    <div data-building-document-folders>
      <p className="mb-3 mt-4 flex items-center gap-2 text-base font-medium text-foreground">
        <ArrowDownRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        {t.buildings.scope.chooseProjectFolders}
      </p>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,300px),1fr))] gap-3">
        {shown.map((project) => (
          <button
            key={project.id}
            type="button"
            data-document-project-card={project.id}
            className={cn(
              PROJECT_TILE_CLASS,
              PROJECT_TILE_HEIGHT_CLASS,
              PROJECT_TILE_INTERACTIVE_CLASS,
              "min-w-0 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            )}
            onClick={() => onOpen(project.id)}
          >
            <span className="flex w-full min-w-0 items-center gap-2">
              <Folder className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
              <ProjectStatusBadge status={project.status} variant="dot" />
              <span
                className="min-w-0 flex-1 truncate text-[14px] font-semibold leading-snug"
                title={project.name}
              >
                {project.name}
              </span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
